// File Metadata:
// Filename: baseApi.js
// Version: 1.01
// Created: 2025-06-16
// Last Modified: 2025-10-22
// Authors: Dieuwer Descheemaecker, GitHub Copilot
// Description: Base RTK Query API slice with authentication and token refresh handling - Removed default Content-Type header to allow FormData uploads

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { logout, setCredentials } from '../slices/userSlice';

// Get base URL from environment or default with fallback logic
const apiBaseUrl = import.meta.env.VITE_APP_API_URL || (
  import.meta.env.DEV 
    ? 'http://localhost:5010'
    : 'https://familysync.dieuwerdescheemaecker.ovh'
);

console.log(`Using API base URL: ${apiBaseUrl}`);

const baseUrl = `${apiBaseUrl}/api`;

// Base query with headers and authentication
const baseQuery = fetchBaseQuery({
  baseUrl,
  credentials: 'include', // Include cookies for refresh token
  prepareHeaders: (headers, { getState }) => {
    // Get token from Redux state
    const token = getState().user.token;
    
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    
    // Don't set Content-Type here - let individual requests set it as needed
    // This allows FormData requests to set their own multipart/form-data headers
    
    return headers;
  },
});

// Enhanced base query with automatic token refresh (migrating axiosInstance logic)
const baseQueryWithReauth = async (args, api, extraOptions) => {
  // Make the initial request
  let result = await baseQuery(args, api, extraOptions);
  
  // If we get a 401 Unauthorized, try to refresh the token
  if (result.error && result.error.status === 401) {
    console.log('Access token expired, attempting refresh...');
    
    // Try to refresh the token
    const refreshResult = await baseQuery(
      {
        url: '/auth/refresh',
        method: 'POST',
      },
      api,
      extraOptions
    );
    
    if (refreshResult.data) {
      // Store the new token
      api.dispatch(setCredentials(refreshResult.data));
      
      // Retry the original request with the new token
      result = await baseQuery(args, api, extraOptions);
    } else {
      // Refresh failed, logout the user
      console.log('Token refresh failed, logging out user');
      api.dispatch(logout());
      
      // Redirect to login page
      window.location.href = '/login';
    }
  }
  
  return result;
};

// Create the main API slice
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  
  // Define tag types for cache invalidation
  tagTypes: [
    'User',           // Current user data
    'Users',          // Family members list
    'Profile',        // User profile data
    'Checklist',      // Checklist templates
    'ChecklistItem',  // Individual checklist items
    'ChecklistAssignment', // User checklist assignments
    'Chore',          // Chore instances
    'ChoreTemplate',  // Chore templates
    'Chat',           // Chat rooms
    'Message',        // Chat messages
    'Todo',           // Todo items
    'Reward',         // Rewards
    'RewardHistory',  // Reward redemption history
    'Redemption',     // Reward redemption records
    'GroupGoal',      // Group goal rewards
    'Family',         // Family data
    'Activity',       // User activities
    'Attachment',     // File attachments and uploads
    'Widget',         // Available widgets
    'WidgetData',     // Widget data
    'WidgetPreferences', // User widget preferences
    'Statistics',     // User statistics
    'Achievements',   // User achievements
    'Analytics',      // Profile analytics
    'Game',           // Game Gallery games
  ],
  
  // Keep unused data for 60 seconds by default
  keepUnusedDataFor: 60,
  
  // Refetch on mount if data is older than 30 seconds
  refetchOnMountOrArgChange: 30,
  
  // Refetch on focus if data is older than 60 seconds
  refetchOnFocus: true,
  
  // Empty endpoints - will be injected by individual API slices
  endpoints: () => ({}),
});

// Export hooks for cache utilities
export const {
  util: { getRunningQueriesThunk, invalidateTags },
} = baseApi;

// Export the reducer to be used in store configuration
export default baseApi.reducer;
